import { DataKeys, DataStructure } from "../../types";
import { useLang } from "../Context";
import { VideoPlayer } from "../tools/Viewer";
import { TitleSubtitle } from "../display/TextFormat";
import { HorizontalScrollWithTopRightArrows, SpacingFrame } from "../display/Frame";
import {
    healthChipTealTag,
} from "../health/healthVisualStyles";

/** Static card — no hover lift/shadow (avoids clip inside overflow-x scroll + MainFrame). */
const agentsVideoTile =
    "flex w-full min-w-0 flex-col rounded-2xl border border-grey-100 bg-white p-5 text-left shadow-[0_8px_24px_rgba(16,24,40,0.06)] transition-shadow duration-300 hover:shadow-[0_12px_36px_rgba(16,24,40,0.1)] sm:p-6";

type VideoAspectRatio = "16/9" | "9/16";

function resolveAspect(item: DataKeys): VideoAspectRatio {
    const aspect = item["aspect"];
    if (aspect === "9/16" || aspect === "16/9") {
        return aspect;
    }
    const url = String(item["video"] ?? "");
    if (url.includes("/shorts/")) {
        return "9/16";
    }
    return "16/9";
}

interface AgentsVideoGalleryProps {
    title?: string;
    subtitle?: string;
    items: DataStructure;
}

export function AgentsVideoGallery({ title, subtitle, items }: AgentsVideoGalleryProps) {
    const { lang } = useLang();
    const keys = Object.keys(items).sort((a, b) => Number(a) - Number(b));

    if (keys.length === 0) return null;

    const isSingle = keys.length === 1;

    const renderVideoItem = (key: string) => {
        const item = items[key] as DataKeys;
        const videoUrl = String(item["video"] ?? "");
        const videoSubtitle = item["subtitle"]?.[lang] ?? "";
        const chip = item["chip"]?.[lang] ?? "";
        const venue = item["venue"]?.[lang] ?? "";
        const aspect = resolveAspect(item);
        const isPortrait = aspect === "9/16";

        const columnWidth = isPortrait
            ? "w-[min(72vw,17.5rem)]"
            : "w-[min(88vw,34rem)]";

        return (
            <figure
                key={key}
                role="listitem"
                className={`m-0 flex h-full shrink-0 self-stretch py-3 ${columnWidth} ${
                    isSingle ? "max-w-full" : ""
                }`}
            >
                <article className={`${agentsVideoTile} h-full min-h-0 flex-1`}>
                    <div className="flex h-full min-h-0 flex-col gap-3">
                        <div className="flex w-full flex-col items-start gap-1.5">
                            {venue ? (
                                <span className={healthChipTealTag}>
                                    {venue}
                                </span>
                            ) : null}
                            {chip ? (
                                <figcaption className="text-left text-[11px] font-bold uppercase tracking-[0.14em] text-teal-600">
                                    {chip}
                                </figcaption>
                            ) : null}
                        </div>
                        {videoSubtitle ? (
                            <p className="w-full text-left text-base font-semibold leading-snug text-grey-900">
                                {videoSubtitle}
                            </p>
                        ) : null}
                        <div
                            className={`mt-auto shrink-0 overflow-hidden rounded-2xl bg-grey-900/5 ${
                                isPortrait ? "mx-auto w-full" : "w-full"
                            }`}
                        >
                            <VideoPlayer
                                url={videoUrl}
                                aspectRatio={aspect}
                            />
                        </div>
                    </div>
                </article>
            </figure>
        );
    };

    return (
        <SpacingFrame spacing={24}>
            {title ? (
                <TitleSubtitle title={title} subtitle={subtitle} left spacing={12} />
            ) : null}

            {isSingle ? (
                <div className="flex justify-center" role="list" aria-label={title}>
                    {renderVideoItem(keys[0])}
                </div>
            ) : (
                <div className="px-1 pb-2">
                    <HorizontalScrollWithTopRightArrows
                        arrowStyle={{ color: "#3299A0" }}
                    >
                        {keys.map((key) => renderVideoItem(key))}
                    </HorizontalScrollWithTopRightArrows>
                </div>
            )}

            {!isSingle ? (
                <p className="text-xs text-grey-400 lg:hidden">
                    {lang === "Zh"
                        ? "左右滑動查看更多影片"
                        : lang === "Jp"
                          ? "左右にスワイプして動画を見る"
                          : "Swipe sideways to see more videos"}
                </p>
            ) : null}
        </SpacingFrame>
    );
}
